import { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Dialog } from '../ui/dialog';
import { Input } from '../ui/input';
import { spaceErrorMessage } from '../../lib/api';

/**
 * One name, for New folder and Rename alike.
 *
 * The server owns the naming rules (allowed characters, case-insensitive uniqueness, depth), so this
 * checks nothing beyond "not empty" and shows the server's own sentence when it refuses — a second copy
 * of the rules here would drift from the one that decides.
 */
export function NameDialog({
  open, title, label, initial, submitLabel, onSubmit, onClose,
}: {
  open: boolean;
  title: string;
  label: string;
  initial: string;
  submitLabel: string;
  onSubmit: (name: string) => Promise<void>;
  onClose: () => void;
}) {
  const [name, setName] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setName(initial);
      setError(null);
      setBusy(false);
    }
  }, [open, initial]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed || busy) return;
    setBusy(true);
    setError(null);
    try {
      await onSubmit(trimmed);
      onClose();
    } catch (err) {
      setError(spaceErrorMessage(err));
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onClose={onClose} title={title} className="max-w-md">
      <form onSubmit={submit} className="space-y-3">
        <label className="block">
          <span className="block text-[11px] font-semibold text-ink-faint uppercase tracking-wide mb-1.5">{label}</span>
          <Input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            onFocus={(e) => {
              // Select the stem, not the extension: renaming "notes.md" almost always means changing "notes".
              const dot = e.target.value.lastIndexOf('.');
              e.target.setSelectionRange(0, dot > 0 ? dot : e.target.value.length);
            }}
            maxLength={255}
          />
        </label>
        {error && <p className="text-xs text-danger-text">{error}</p>}
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" size="sm" disabled={busy || !name.trim()}>
            {busy ? 'Saving…' : submitLabel}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
